import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { MaskitoElementPredicate, MaskitoOptions } from '@maskito/core';
import { select, Store } from '@ngrx/store';

import { compact } from 'lodash-es';
import { Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { PrimarySubscriptionPlanAccessAliasEnum } from '../../enums/monetization';
import { ProfileAdvancedSearchTabEnum, ProfileSearchEnum } from '../../enums/profile-search';
import { MonetizationService } from '../../misc/services/monetization.service';
import * as fromPropertyProfileActions from '../../store/actions/property-profile.actions';
import * as fromSettingActions from '../../store/actions/setting.actions';
import * as fromSettingSelectors from '../../store/selectors/setting.selectors';
import { SearchableSelectOption } from '../../typings/searchable-select';

interface AdvancedFormGroup {
  address: FormControl<string | null>;
  city: FormControl<string | null>;
  state: FormControl<string | null>;
  unitNumber: FormControl<string | null>;
  zip: FormControl<string | null>;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-property-search-advanced',
  standalone: false,
  templateUrl: './advanced.page.html'
})
export class AdvancedPageComponent {
  private store = inject(Store);
  private monetizationService = inject(MonetizationService);

  availableStates$: Observable<SearchableSelectOption[]>;

  readonly zipMask: MaskitoOptions = {
    mask: [/\d/, /\d/, /\d/, /\d/, /\d/]
  };

  readonly maskPredicate: MaskitoElementPredicate = async (el) =>
    (el as unknown as HTMLIonInputElement).getInputElement();

  form: FormGroup<AdvancedFormGroup> = new FormGroup<AdvancedFormGroup>({
    address: new FormControl(null, [Validators.required]),
    city: new FormControl(null),
    state: new FormControl(null),
    unitNumber: new FormControl(null),
    zip: new FormControl(null, [Validators.required])
  });
  selectedTab = ProfileAdvancedSearchTabEnum.ZipCode;
  pageTabs = ProfileAdvancedSearchTabEnum;

  constructor() {
    this.availableStates$ = this.store.pipe(select(fromSettingSelectors.selectAvailableStates));
    this.store
      .pipe(select(fromSettingSelectors.selectSelectedState), distinctUntilChanged())
      .subscribe((selectedState) => {
        if (this.form.controls.state.value !== selectedState) {
          this.form.controls.state.setValue(selectedState, { emitEvent: false });
        }
      });
    this.form.controls.state.valueChanges.pipe(distinctUntilChanged()).subscribe((value) => {
      this.store.dispatch(fromSettingActions.updateSelectedState({ payload: { state: value } }));
    });
  }

  segmentChange(event: CustomEvent) {
    this.selectedTab = event.detail.value;

    switch (this.selectedTab) {
      case this.pageTabs.CityState:
        this.form.controls.zip.setValidators(null);
        this.form.controls.city.setValidators([Validators.required]);
        this.form.controls.state.setValidators([Validators.required]);
        break;

      default:
        this.form.controls.zip.setValidators([Validators.required, Validators.minLength(5), Validators.maxLength(5)]);
        this.form.controls.city.setValidators(null);
        this.form.controls.state.setValidators(null);
        break;
    }

    this.form.controls.zip.updateValueAndValidity();
    this.form.controls.city.updateValueAndValidity();
    this.form.controls.state.updateValueAndValidity();
  }

  async onSubmit() {
    const hasAccess = await this.monetizationService.accessFeature([PrimarySubscriptionPlanAccessAliasEnum.A]);
    if (hasAccess) {
      const formValues = this.form.getRawValue();
      this.store.dispatch(
        fromPropertyProfileActions.loadProfileByAddress({
          payload: {
            address: compact([formValues.address, formValues.unitNumber]).join(' '),
            searchType: ProfileSearchEnum.PropertySearch,
            zip:
              this.selectedTab === this.pageTabs.ZipCode
                ? formValues.zip || ''
                : compact([formValues.city, formValues.state]).join(', ')
          }
        })
      );
    }
  }
}
